import { inject, Injectable } from '@angular/core';
import { AutopropBackend, Response, TasksService } from 'app/shared/backend';
import { Geometry } from 'app/shared/ll/models/geometry';
import { map, mergeMap } from 'rxjs';
import { Preset } from '../cma/cma.models';
import { Area, AreaItem, DrawnPolygon } from './areas.model';


type Update = { updatedAt: string; expiresAt: string | null };


@Injectable({
    providedIn: 'root',
})
export class AreasApi {

    private backend = inject(AutopropBackend);

    private tasksService = inject(TasksService);

    create(payload: { type: 'selection' } | { type: 'geometry', geometry: DrawnPolygon[] }) {
        return this.backend.post<Response<string>>('/api/area/create', payload).pipe(map(r => r.payload));
    }

    private editableFields(area: Partial<Area>) {
        return {
            id: area.id,
            name: area.name,
            type: area.type,
            selection: area.selection,
            geometry: area.geometry,
            notifDef: area.notifDef,
            notifEnabled: area.notifEnabled,
        };
    }

    update(area: Partial<Area>) {
        return this.backend.post('/api/area/update', this.editableFields(area));
    }

    save(area: Partial<Area>) {
        return this.backend.post('/api/area/save', { area: this.editableFields(area) });
    }

    rename(areaId: string, name: string) {
        return this.backend.post<Response<Update>>('/api/area/rename', { areaId, name });
    }

    renew(areaId: string) {
        return this.backend.post<Response<Update>>('/api/area/renew', { areaId });
    }

    deleteArea(areaId: string) {
        return this.backend.post('/api/area/delete', { areaId });
    }

    loadAreas() {
        return this.backend.get<Response<AreaItem[]>>('/api/areas').pipe(map(r => r.payload));
    }

    loadAreaHistory(areaId: string) {
        return this.tasksService.createTasks(`/api/area/${areaId}/history`, {}).pipe(mergeMap(r => r.tasks[0].result));
    }

    countMls(areaId: string, mlsParams?: Record<string, unknown>) {
        return this.tasksService.createTasks('/api/area/count-mls', { areaId, mlsParams }).pipe(mergeMap(r => r.tasks[0].result));
    }

    savePreset(preset: Preset) {
        return this.backend.post('/api/area/save-preset', { preset });
    }

    locatePid(pid: string) {
        return this.backend.get(`/api/parcel/pid/${pid}`).pipe(map(r => r.payload.parcel));
    }

    locateBl(bls: Array<{ uniqueId: string; upid: string; geometry: Geometry }>) {
        return this.backend.post(`/api/parcel/bl`, { bls }).pipe(map(r => r.payload));
    }

    locateMls(ulids: string[]) {
        return this.backend.post(`/api/parcel/mls`, { ulids }).pipe(map(r => r.payload));
    }

    csvExport(notifications: string[]) {
        return this.backend.postBlob('/api/area/csv-export', { notifications });
    }
}
